import { FC, ReactNode, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { Check, Copy } from 'lucide-react';
import dayjs from 'dayjs';
import { IntegrationError } from '../../@types';

const COPIED_FEEDBACK_MS = 2000;

const ERP_ERROR_LABELS: Record<string, string> = {
  '1': 'Saldo de Estoque',
  '2': 'Período Bloqueado',
  '3': 'Chave Estrangeira',
  '4': 'Master Não Encontrado',
  '5': 'Validação do Modelo',
};

function formatTooltipDate(date: string): string {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : date;
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function formatCodigoErro(codigo: string | null | undefined): string | null {
  if (!hasValue(codigo)) return null;
  const label = ERP_ERROR_LABELS[String(codigo)];
  return label ? `${codigo} – ${label}` : String(codigo);
}

const Line = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    {label} <b>{value}</b>
  </div>
);

function buildCopyText(
  erro: IntegrationError,
  codigoFormatado: string | null,
): string {
  const lines: string[] = [];
  if (hasValue(erro.idLocal)) lines.push(`ID: ${erro.idLocal}`);
  lines.push(`Propriedade: ${erro.propriedade}`);
  if (hasValue(erro.model)) lines.push(`Model: ${erro.model}`);
  if (hasValue(erro.origem)) lines.push(`Origem: ${erro.origem}`);
  lines.push(`Data: ${formatTooltipDate(erro.createdAt)}`);
  if (codigoFormatado) lines.push(`Código do Erro: ${codigoFormatado}`);
  if (hasValue(erro.mensagemErro)) {
    lines.push(`Mensagem de Erro: ${erro.mensagemErro}`);
  }
  return lines.join('\n');
}

const IntegrationErrorTooltipContent: FC<{ erro: IntegrationError }> = ({
  erro,
}) => {
  const [copied, setCopied] = useState(false);
  const codigoFormatado = formatCodigoErro(erro.codigoErro);

  const handleCopy = useImmutableCallback(() => {
    navigator.clipboard
      .writeText(buildCopyText(erro, codigoFormatado))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
      });
  });

  return (
    <div className="integration-error-tooltip-content">
      {hasValue(erro.idLocal) && <Line label="ID:" value={erro.idLocal} />}
      <Line label="Propriedade:" value={erro.propriedade} />
      {hasValue(erro.model) && <Line label="Model:" value={erro.model} />}
      {hasValue(erro.origem) && <Line label="Origem:" value={erro.origem} />}
      <Line label="Data:" value={formatTooltipDate(erro.createdAt)} />
      {codigoFormatado && (
        <Line label="Código do Erro:" value={codigoFormatado} />
      )}
      {hasValue(erro.mensagemErro) && (
        <Line label="Mensagem de Erro:" value={erro.mensagemErro} />
      )}
      <div className="integration-error-tooltip-actions">
        <button
          type="button"
          className="integration-error-tooltip-copy-btn"
          onClick={handleCopy}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? 'Copiado!' : 'Copiar'}</span>
        </button>
      </div>
    </div>
  );
};

export default IntegrationErrorTooltipContent;
